import type { CountyPriorityDisplayRow } from './layerStatsFormat';

/**
 * Set false (or delete usages) to restore pre-demo Analytics Filters UI: Event
 * column returns, demo Include control / capacity widget hide, Priority Ranked
 * Counties AGOL order restored.
 */
export const LAYER_STATS_DEMO_FILTERS_ENABLED = true;

/**
 * Multiplier so Dyer's need bar clearly exceeds Shelby's when Resource Capacity
 * is on.
 */
const DYER_NEED_MULTIPLIER_OVER_SHELBY = 1.75;

export type DemoFloodFilterKey =
  | 'demoResourceCapacity'
  | 'demoWaterLevels'
  | 'demoPopulationImpacted'
  | 'demoPowerOutages';

export type DemoFloodFilterOption = {
  key: DemoFloodFilterKey;
  label: string;
  description: string;
};

/** Include multiselect options + tooltip copy for the Analytics demo Filters. */
export const DEMO_FLOOD_FILTER_OPTIONS: DemoFloodFilterOption[] = [
  {
    key: 'demoResourceCapacity',
    label: 'Resource Capacity',
    description:
      'National Guard and partner staging capacity by county (personnel, vehicles, and sustainment) used to score population service capacity under surge demand.',
  },
  {
    key: 'demoWaterLevels',
    label: 'Water Levels',
    description:
      'Flood depth and hydrograph context from FloodingBins / water-level observations used in flood impact scoring.',
  },
  {
    key: 'demoPopulationImpacted',
    label: 'Population Impacted',
    description:
      'Estimated population exposure and occupancy attributes associated with flooded structures and bins.',
  },
  {
    key: 'demoPowerOutages',
    label: 'Power Outages',
    description:
      'Ting Outages AOI features (outage/restoration state, recovery %, area, and display date) for stuck and largest outages.',
  },
];

export type PopulationServiceCapacityRow = {
  county: string;
  /** People that can be served with available resources (demo units). */
  capacity: number;
};

/**
 * Hardcoded TN demo capacities. Dyer is intentionally much lower than Shelby so
 * Resource Capacity elevates it as the highest-need county in the narrative.
 */
export const DEMO_POPULATION_SERVICE_CAPACITY_ROWS: PopulationServiceCapacityRow[] =
  [
    { county: 'Shelby', capacity: 18500 },
    { county: 'Madison', capacity: 9200 },
    { county: 'Gibson', capacity: 6100 },
    { county: 'Obion', capacity: 5400 },
    { county: 'Dyer', capacity: 900 },
  ];

const DYER_COUNTY_MATCH = /dyer/i;
const SHELBY_COUNTY_MATCH = /shelby/i;

export function isDyerCountyLabel(label: string): boolean {
  return DYER_COUNTY_MATCH.test(label);
}

function isShelbyCountyLabel(label: string): boolean {
  return SHELBY_COUNTY_MATCH.test(label);
}

function scalePrioritySegments(
  segments: CountyPriorityDisplayRow['byPriority'],
  fromTotal: number,
  toTotal: number
): CountyPriorityDisplayRow['byPriority'] {
  if (!segments.length) return [];
  if (fromTotal <= 0) {
    return [{ priority: segments[0]?.priority || '5', count: toTotal }];
  }
  const scaled = segments.map((seg) => ({
    priority: seg.priority,
    count: Math.max(0, Math.round((seg.count / fromTotal) * toTotal)),
  }));
  const scaledSum = scaled.reduce((sum, seg) => sum + seg.count, 0);
  const drift = toTotal - scaledSum;
  if (drift !== 0 && scaled.length > 0) {
    scaled[0] = { ...scaled[0], count: Math.max(0, scaled[0].count + drift) };
  }
  return scaled;
}

function inflateDyerNeedRow(
  dyer: CountyPriorityDisplayRow,
  shelbyTotal: number
): CountyPriorityDisplayRow {
  const targetTotal = Math.max(
    Math.round(shelbyTotal * DYER_NEED_MULTIPLIER_OVER_SHELBY),
    shelbyTotal + 1,
    1
  );
  const fromTotal = dyer.total > 0 ? dyer.total : targetTotal;
  return {
    label: dyer.label || 'Dyer',
    total: targetTotal,
    byPriority: scalePrioritySegments(dyer.byPriority, fromTotal, targetTotal),
  };
}

/**
 * When Resource Capacity demo filter is on, put Dyer first and inflate its need
 * total well above Shelby. Does not change AGOL data — client only.
 */
export function applyDemoResourceCapacityCountyBoost(
  rows: CountyPriorityDisplayRow[],
  enabled: boolean
): CountyPriorityDisplayRow[] {
  if (!LAYER_STATS_DEMO_FILTERS_ENABLED || !enabled) {
    return rows;
  }

  const source = rows.map((row) => ({
    ...row,
    byPriority: row.byPriority.map((seg) => ({ ...seg })),
  }));

  const shelbyTotal =
    source.find((row) => isShelbyCountyLabel(row.label))?.total ??
    source[0]?.total ??
    0;

  const dyerIdx = source.findIndex((row) => isDyerCountyLabel(row.label));
  let dyer: CountyPriorityDisplayRow;
  if (dyerIdx >= 0) {
    [dyer] = source.splice(dyerIdx, 1);
  } else {
    dyer = {
      label: 'Dyer',
      total: Math.max(shelbyTotal, 1),
      byPriority: source[0]?.byPriority?.length
        ? source[0].byPriority.map((seg) => ({ ...seg }))
        : [{ priority: '5', count: Math.max(shelbyTotal, 1) }],
    };
  }

  return [inflateDyerNeedRow(dyer, shelbyTotal), ...source];
}

/** Capacity rows sorted ascending so Dyer (lowest) appears first in the widget. */
export function populationServiceCapacityRowsSorted(): PopulationServiceCapacityRow[] {
  return [...DEMO_POPULATION_SERVICE_CAPACITY_ROWS].sort(
    (a, b) => a.capacity - b.capacity
  );
}
